import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Line, ResponsiveContainer, ReferenceLine } from 'recharts';
import { FVProfile, Trial } from '../types';

interface FVChartProps {
  trials: Trial[];
  fvProfile: FVProfile;
  vFreeMs: number;
}

export const FVChart: React.FC<FVChartProps> = ({ trials, fvProfile, vFreeMs }) => {
  // Prepare data points
  const dataPoints = [
    ...trials.map(t => ({
      velocity: t.v_max_ms,
      force: t.load_kgf * 9.80665,
      load: t.load_kgf
    })),
    {
      velocity: vFreeMs,
      force: 0,
      load: 0
    }
  ];

  // Generate regression line points
  const regressionLine = [];
  const minV = 0;
  const maxV = fvProfile.V0;
  for (let v = minV; v <= maxV; v += 0.5) {
    regressionLine.push({
      velocity: v,
      force: fvProfile.intercept + fvProfile.slope * v
    });
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-theme-surface-2 border border-theme-border text-theme-text rounded-md px-2 py-1 text-xs shadow-card">
          <p className="font-medium tabular-nums">{`Load: ${data.load} kgf`}</p>
          <p className="tabular-nums">{`Velocity: ${data.velocity.toFixed(2)} m/s`}</p>
          <p className="tabular-nums">{`Force: ${data.force.toFixed(1)} N`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-4">Force–Velocity Profile</h3>
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#3F4451" />
            <XAxis 
              type="number" 
              dataKey="velocity" 
              name="Velocity"
              unit=" m/s"
              domain={[0, Math.max(fvProfile.V0, vFreeMs) * 1.1]}
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
              axisLine={{ stroke: '#3F4451' }}
              tickLine={{ stroke: '#3F4451' }}
            />
            <YAxis 
              type="number" 
              dataKey="force" 
              name="Force"
              unit=" N"
              domain={[0, fvProfile.F0_N * 1.1]}
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
              axisLine={{ stroke: '#3F4451' }}
              tickLine={{ stroke: '#3F4451' }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Data points */}
            <Scatter 
              data={dataPoints} 
              fill="#64748b" 
              strokeWidth={2}
              stroke="#475569"
            />
            
            {/* Regression line - using red accent */}
            <Scatter 
              data={regressionLine} 
              fill="none" 
              stroke="#E11D48"
              strokeWidth={2}
              line
            />
            
            {/* Reference lines */}
            <ReferenceLine x={fvProfile.V0} stroke="#64748b" strokeDasharray="5 5" />
            <ReferenceLine y={fvProfile.F0_N} stroke="#64748b" strokeDasharray="5 5" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 grid grid-cols-2 gap-4 text-base text-theme-muted">
        <div className="tabular-nums">
          <span className="font-medium text-theme-text">V₀:</span> {fvProfile.V0.toFixed(2)} m/s
        </div>
        <div className="tabular-nums">
          <span className="font-medium text-theme-text">F₀:</span> {fvProfile.F0_N.toFixed(1)} N
        </div>
        <div className="tabular-nums">
          <span className="font-medium text-theme-text">R²:</span> {fvProfile.R2.toFixed(3)}
        </div>
        <div className="tabular-nums">
          <span className="font-medium text-theme-text">Pmax:</span> {(fvProfile.Pmax / 1000).toFixed(2)} kW
        </div>
      </div>
    </div>
  );
};